import React, { useState, useEffect, useCallback } from 'react';
import { devError } from '../../lib/devLog';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Search, Trash2, X, Loader, Check, Ban, ShoppingBag, Clock,
} from 'lucide-react';
import ArtworkList from './ArtworkList';
import AIRewriteModal from './AIRewriteModal';
import ArtworkForm from './ArtworkForm';
import { INITIAL_FORM_DATA, formDataFromArtwork } from './artworkFormData';
import useSpeechToText from './useSpeechToText';
import { withAdminAuth } from '../../lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 60;  // ~5 rows of 12 tiles — keeps a full refresh well under Cloudflare's per-request threshold

const ArtworkManager = () => {
  const [artworks, setArtworks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDescription, setFilterDescription] = useState('all');
  const [page, setPage] = useState(0);
  const [counts, setCounts] = useState({ total: 0, missing: 0, thin: 0, good: 0 });
  const [loading, setLoading] = useState(false);
  const [rewriteState, setRewriteState] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // Speech-to-text streams transcripts straight into the description field.
  const handleTranscript = useCallback((transcript) => {
    setFormData((prev) => ({ ...prev, description: transcript }));
  }, []);
  const { isListening, toggle: toggleVoiceInput } = useSpeechToText(handleTranscript);

  useEffect(() => {
    fetchArtists();
  }, []);

  // Debounce the search input so we don't spam the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Reset back to page 0 whenever a filter changes — the current page's
  // slice would otherwise land outside the filtered result set.
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, filterCategory, filterDescription]);

  const buildQueryParams = useCallback((extra = {}) => {
    const params = new URLSearchParams({ include_hidden: '1' });
    if (debouncedQuery) params.set('q', debouncedQuery);
    if (filterCategory !== 'all') params.set('category', filterCategory);
    if (filterDescription !== 'all') params.set('description_status', filterDescription);
    for (const [k, v] of Object.entries(extra)) params.set(k, String(v));
    return params.toString();
  }, [debouncedQuery, filterCategory, filterDescription]);

  const fetchArtworks = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQueryParams({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      const [list, cntResp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/artworks?${qs}`),
        axios.get(`${BACKEND_URL}/api/artworks/counts?${buildQueryParams()}`),
      ]);
      setArtworks(list.data);
      setCounts(cntResp.data);
    } catch (error) {
      devError('Error fetching artworks:', error);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, page]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/artists?include_hidden=1`);
      setArtists(response.data);
    } catch (error) {
      devError('Error fetching artists:', error);
    }
  };

  // Open the create form automatically when navigated with ?new=1
  // (e.g. from the Dashboard "Add Artwork" quick action).
  const _location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(_location.search);
    if (params.get('new') === '1') {
      setEditingArtwork(null);
      setShowForm(true);
    }
  }, [_location.search]);

  const generateAIDescription = async () => {
    if (!formData.artistName || !formData.title) {
      alert('Please enter artist name and title first');
      return;
    }
    setIsGeneratingDescription(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/artwork-description`,
        {
          artist_name: formData.artistName,
          title: formData.title,
          medium: formData.medium,
          year: formData.year,
          additional_info: `${formData.dimensions} ${formData.exhibition_history}`,
        },
        withAdminAuth(),
      );
      setFormData((prev) => ({ ...prev, description: response.data.description }));
    } catch (error) {
      devError('Error generating description:', error);
      alert('Failed to generate description');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
      };
      if (editingArtwork) {
        await axios.put(
          `${BACKEND_URL}/api/admin/artworks/${editingArtwork.id}`,
          submitData,
          withAdminAuth(),
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/artworks`,
          submitData,
          withAdminAuth(),
        );
      }
      fetchArtworks();
      resetForm();
    } catch (error) {
      devError('Error saving artwork:', error);
      alert(`Error saving artwork: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEdit = (artwork) => {
    setEditingArtwork(artwork);
    setFormData(formDataFromArtwork(artwork));
    setShowForm(true);
  };

  const handleDelete = async (artworkId) => {
    if (!window.confirm('Delete this artwork?')) return;

    const target = artworks.find((a) => a.id === artworkId);
    const title = target?.title || 'Artwork';

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/artworks/${artworkId}`,
        withAdminAuth(),
      );
      // Optimistic UI: drop the row immediately so the bin click feels instant,
      // then refetch to stay authoritative.
      setArtworks((prev) => prev.filter((a) => a.id !== artworkId));
      await fetchArtworks();
      toast.success(`Deleted "${title}"`);
    } catch (error) {
      devError('Error deleting artwork:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let msg;
      if (status === 401) msg = 'Your admin session has expired. Please log in again.';
      else if (status === 403) msg = 'Security check failed (CSRF). Refresh the page and try again.';
      else if (status === 404) msg = 'That artwork no longer exists. The list will refresh.';
      else if (status === 405) msg = 'Server route mis-configured. Contact the developer.';
      else msg = typeof detail === 'string' ? detail : (error.message || 'Unknown error');
      toast.error(`Could not delete artwork: ${msg}`);
      if (status === 404) fetchArtworks();
    }
  };

  // ── Bulk selection helpers ──
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const enterSelectionMode = () => {
    setSelectionMode(true);
    setSelectedIds(new Set());
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(
      `Delete ${count} artwork${count === 1 ? '' : 's'}? This cannot be undone.`,
    )) return;

    setBulkDeleting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/artworks/bulk-delete`,
        { ids: Array.from(selectedIds) },
        withAdminAuth(),
      );
      await fetchArtworks();
      exitSelectionMode();
      toast.success(`Deleted ${res.data.deleted} artwork${res.data.deleted === 1 ? '' : 's'}`);
    } catch (error) {
      devError('Bulk delete failed:', error);
      toast.error(`Bulk delete failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Availability toggle (single) — cycles: available → on_hold → sold → available ──
  const handleToggleAvailability = async (artwork) => {
    const currentStatus =
      artwork.status ||
      (artwork.available === false ? 'sold' : 'available');
    const NEXT = { available: 'on_hold', on_hold: 'sold', sold: 'available' };
    const nextStatus = NEXT[currentStatus] || 'available';
    const nextAvailable = nextStatus === 'available';

    // Optimistic UI flip
    setArtworks((prev) =>
      prev.map((a) =>
        a.id === artwork.id ? { ...a, status: nextStatus, available: nextAvailable } : a,
      ),
    );
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/artworks/${artwork.id}/availability`,
        { status: nextStatus },
        withAdminAuth(),
      );
      const label =
        nextStatus === 'on_hold' ? 'On Hold' :
        nextStatus === 'sold' ? 'Sold' : 'Available';
      toast.success(`"${artwork.title}" → ${label}`);
    } catch (error) {
      devError('Availability toggle failed:', error);
      toast.error(`Could not update: ${error.response?.data?.detail || error.message}`);
      fetchArtworks();
    }
  };

  // ── Featured pin toggle (single) — pins artwork to Homepage "Latest" shelf ──
  const handleToggleFeatured = async (artwork) => {
    const nextFeatured = !artwork.featured;
    // Optimistic UI flip
    setArtworks((prev) =>
      prev.map((a) => (a.id === artwork.id ? { ...a, featured: nextFeatured } : a)),
    );
    try {
      // Send the full artwork body so PUT round-trips cleanly with existing fields
      await axios.put(
        `${BACKEND_URL}/api/admin/artworks/${artwork.id}`,
        { ...artwork, featured: nextFeatured },
        withAdminAuth(),
      );
      toast.success(
        nextFeatured
          ? `Pinned "${artwork.title}" to Homepage`
          : `Unpinned "${artwork.title}"`,
      );
    } catch (error) {
      devError('Featured toggle failed:', error);
      toast.error(`Could not update: ${error.response?.data?.detail || error.message}`);
      fetchArtworks();
    }
  };

  // ── Availability toggle (bulk) ──
  const handleBulkSetStatus = async (nextStatus) => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    const label =
      nextStatus === 'on_hold' ? 'On Hold' :
      nextStatus === 'sold' ? 'Sold' : 'Available';
    if (!window.confirm(`Mark ${count} artwork${count === 1 ? '' : 's'} as ${label}?`)) return;

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/artworks/bulk-availability`,
        { ids: Array.from(selectedIds), status: nextStatus },
        withAdminAuth(),
      );
      await fetchArtworks();
      exitSelectionMode();
      toast.success(`${res.data.updated} artwork${res.data.updated === 1 ? '' : 's'} marked as ${label}`);
    } catch (error) {
      devError('Bulk availability failed:', error);
      toast.error(`Bulk update failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  // ── AI rewrite (hallucination-resistant, fact-locked) ──
  const handleRewriteOpen = async (artwork) => {
    setRewriteState({ artwork, loading: true });
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/ai/artwork-description-rewrite/${artwork.id}`,
        {},
        withAdminAuth(),
      );
      setRewriteState({
        artwork,
        loading: false,
        draft: r.data.draft,
        editedDraft: r.data.draft,
        previous: r.data.previous_description,
        wordCount: r.data.word_count,
        validation: r.data.validation,
      });
    } catch (e) {
      setRewriteState({
        artwork,
        loading: false,
        error: e.response?.data?.detail || e.message,
      });
    }
  };

  const handleRewriteSave = async () => {
    if (!rewriteState?.editedDraft) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/ai/artwork-description-rewrite/${rewriteState.artwork.id}/save`,
        { description: rewriteState.editedDraft },
        withAdminAuth(),
      );
      setRewriteState(null);
      fetchArtworks();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const handleRewriteRegenerate = async () => {
    if (!rewriteState?.artwork) return;
    await handleRewriteOpen(rewriteState.artwork);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingArtwork(null);
    setShowForm(false);
  };

  // Artworks are already filtered server-side (search + category + description).
  // Only client-side concern: keep a stable reference for bulk-select "select all visible".
  const filteredArtworks = artworks;

  // Description-quality counts come from the /counts endpoint so they
  // reflect the FULL filtered set — not just the current page.
  const descriptionStats = {
    missing: counts.missing,
    thin: counts.thin,
    good: counts.good,
  };

  const totalPages = Math.max(1, Math.ceil(counts.total / PAGE_SIZE));
  const canPrev = page > 0;
  const canNext = page < totalPages - 1;
  const rangeStart = counts.total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(counts.total, (page + 1) * PAGE_SIZE);

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-light mb-2">Artworks</h1>
          <p className="text-gray-600">Manage your gallery collection</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-3 bg-black text-white rounded-lg flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Artwork
        </motion.button>
      </div>

      <AnimatePresence>
        {showForm && (
          <ArtworkForm
            editingArtwork={editingArtwork}
            formData={formData}
            setFormData={setFormData}
            artists={artists}
            onSubmit={handleSubmit}
            onCancel={resetForm}
            isListening={isListening}
            onToggleVoice={toggleVoiceInput}
            isGenerating={isGeneratingDescription}
            onGenerateAI={generateAIDescription}
          />
        )}
      </AnimatePresence>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search artworks..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="admin-artwork-search-input"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="all">All Categories</option>
            <option value="painting">Painting</option>
            <option value="photography">Photography</option>
            <option value="sculpture">Sculpture</option>
            <option value="print">Print</option>
          </select>
          <select
            value={filterDescription}
            onChange={(e) => setFilterDescription(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            data-testid="filter-description"
            title="Filter by description quality"
          >
            <option value="all">All Descriptions</option>
            <option value="missing">{`⚠ Missing (${descriptionStats.missing})`}</option>
            <option value="thin">{`⚠ Thin · <80 chars (${descriptionStats.thin})`}</option>
            <option value="good">{`✓ Good · 80+ chars (${descriptionStats.good})`}</option>
          </select>
          {!selectionMode ? (
            <button
              type="button"
              onClick={enterSelectionMode}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
              data-testid="bulk-select-enter-btn"
              title="Select multiple artworks for bulk actions (Sold / Available / Delete)"
            >
              <Check className="w-4 h-4" />
              Select
            </button>
          ) : (
            <button
              type="button"
              onClick={exitSelectionMode}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
              data-testid="bulk-select-cancel-btn"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectionMode && (
        <div
          className="sticky top-4 z-30 bg-black text-white rounded-lg shadow-lg p-4 mb-6 flex flex-wrap items-center gap-3"
          data-testid="bulk-action-bar"
        >
          <span className="font-medium" data-testid="bulk-selected-count">
            {selectedIds.size} selected
          </span>
          <span className="text-white/50">·</span>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set(filteredArtworks.map((a) => a.id)))}
            className="text-sm underline underline-offset-4 hover:text-gray-200"
            data-testid="bulk-select-all-btn"
          >
            Select all visible ({filteredArtworks.length})
          </button>
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-sm underline underline-offset-4 hover:text-gray-200"
              data-testid="bulk-clear-btn"
            >
              Clear
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => handleBulkSetStatus('sold')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg flex items-center gap-2 text-sm font-medium"
              data-testid="bulk-mark-sold-btn"
              title="Mark selected as Sold"
            >
              <Ban className="w-4 h-4" />
              Mark Sold
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => handleBulkSetStatus('on_hold')}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg flex items-center gap-2 text-sm font-medium"
              data-testid="bulk-mark-on-hold-btn"
              title="Mark selected as On Hold"
            >
              <Clock className="w-4 h-4" />
              Mark On Hold
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => handleBulkSetStatus('available')}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg flex items-center gap-2 text-sm font-medium"
              data-testid="bulk-mark-available-btn"
              title="Mark selected as Available"
            >
              <ShoppingBag className="w-4 h-4" />
              Mark Available
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0 || bulkDeleting}
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg flex items-center gap-2 text-sm font-medium"
              data-testid="bulk-delete-btn"
            >
              {bulkDeleting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete selected
            </button>
          </div>
        </div>
      )}

      {/* Result meta + pagination top-of-list */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-gray-600" data-testid="admin-artwork-result-meta">
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader className="w-4 h-4 animate-spin" /> Loading…
            </span>
          ) : counts.total === 0 ? (
            <span>No artworks match this filter.</span>
          ) : (
            <span>
              Showing <strong>{rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}</strong>{' '}
              of <strong>{counts.total.toLocaleString()}</strong>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2" data-testid="admin-artwork-pagination">
          <button
            type="button"
            disabled={!canPrev || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-artwork-page-prev"
          >
            ← Previous
          </button>
          <span className="text-sm text-gray-600 px-2" data-testid="admin-artwork-page-indicator">
            Page {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            disabled={!canNext || loading}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-artwork-page-next"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Artworks Grid */}
      <ArtworkList
        artworks={filteredArtworks}
        onEdit={handleEdit}
        onRewrite={handleRewriteOpen}
        onDelete={handleDelete}
        onToggleAvailability={handleToggleAvailability}
        onToggleFeatured={handleToggleFeatured}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
      />

      <AIRewriteModal
        rewriteState={rewriteState}
        onClose={() => setRewriteState(null)}
        onSave={handleRewriteSave}
        onRegenerate={handleRewriteRegenerate}
        onDraftChange={(v) => setRewriteState((s) => ({ ...s, editedDraft: v }))}
      />
    </div>
  );
};

export default ArtworkManager;
